import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { ROUTES } from '../../constants';
import { fetchCategories } from '../../features/categoriesSlice';
import { clearSelectedProduct, fetchProductById, fetchProducts } from '../../features/productsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import PriceDisplay from '../../components/common/PriceDisplay';
import { resolveImageUrl } from '../../utils/image';
import ImageCropDialog from '../../components/admin/ImageCropDialog';

// ISO timestamp → value for a datetime-local input (local time, minute precision)
const toLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const initialForm = {
  name: '',
  sku: '',
  description: '',
  price: '',
  mrp: '',
  salePrice: '',
  promoPrice: '',
  promoStartsAt: '',
  promoEndsAt: '',
  stock: '',
  categoryId: '',
  material: '',
  weight: '',
  length: '1',
  width: '1',
  height: '1',
  isActive: true,
  metaTitle: '',
  metaDescription: '',
  slug: '',
  imageUrls: [] as string[],
  imageUrlInput: '',
  colors: [] as string[],
  colorInput: ''
};

const AdminProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isCreateMode = id === 'new';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { selectedProduct, loading } = useAppSelector((state) => state.products);
  const { items: categories } = useAppSelector((state) => state.categories);

  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropQueue, setCropQueue] = useState<File[]>([]);
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    dispatch(fetchCategories());
    if (isCreateMode) {
      dispatch(clearSelectedProduct());
      setForm(initialForm);
    } else if (id) {
      dispatch(fetchProductById(id));
    }
  }, [dispatch, id, isCreateMode]);

  useEffect(() => {
    if (isCreateMode || !selectedProduct) return;
    setForm({
      name: selectedProduct.name || '',
      sku: selectedProduct.sku || '',
      description: selectedProduct.description || '',
      price: String(selectedProduct.price ?? ''),
      mrp: String(selectedProduct.mrp ?? selectedProduct.salePrice ?? selectedProduct.price ?? ''),
      salePrice: String(selectedProduct.salePrice ?? selectedProduct.price ?? ''),
      promoPrice: selectedProduct.promoPrice != null ? String(selectedProduct.promoPrice) : '',
      promoStartsAt: toLocalInput(selectedProduct.promoStartsAt),
      promoEndsAt: toLocalInput(selectedProduct.promoEndsAt),
      stock: String(selectedProduct.stock ?? ''),
      categoryId: selectedProduct.category?._id || '',
      material: selectedProduct.material || '',
      weight: String(selectedProduct.weight ?? ''),
      length: String(selectedProduct.dimensions?.length ?? 1),
      width: String(selectedProduct.dimensions?.width ?? 1),
      height: String(selectedProduct.dimensions?.height ?? 1),
      isActive: selectedProduct.isActive,
      metaTitle: selectedProduct.name || '',
      metaDescription: selectedProduct.description || '',
      slug: selectedProduct.name.toLowerCase().replace(/\s+/g, '-'),
      imageUrls: selectedProduct.images || [],
      imageUrlInput: '',
      colors: selectedProduct.colors || [],
      colorInput: ''
    });
  }, [isCreateMode, selectedProduct]);

  const hasChanges = useMemo(() => {
    if (isCreateMode) {
      return (
        form.name.trim() !== '' &&
        form.sku.trim() !== '' &&
        form.description.trim() !== '' &&
        form.categoryId !== '' &&
        form.imageUrls.length > 0 &&
        form.material.trim() !== '' &&
        form.colors.length > 0 &&
        Number(form.weight) > 0 &&
        Number(form.stock) >= 0 &&
        Number(form.salePrice) >= 0 &&
        Number(form.mrp) >= Number(form.salePrice)
      );
    }

    if (!selectedProduct) return false;
    return (
      form.name !== selectedProduct.name ||
      form.sku !== selectedProduct.sku ||
      form.description !== selectedProduct.description ||
      Number(form.price) !== selectedProduct.price ||
      Number(form.mrp) !== (selectedProduct.mrp ?? selectedProduct.salePrice ?? selectedProduct.price) ||
      Number(form.salePrice) !== (selectedProduct.salePrice ?? selectedProduct.price) ||
      form.promoPrice !== (selectedProduct.promoPrice != null ? String(selectedProduct.promoPrice) : '') ||
      form.promoStartsAt !== toLocalInput(selectedProduct.promoStartsAt) ||
      form.promoEndsAt !== toLocalInput(selectedProduct.promoEndsAt) ||
      Number(form.stock) !== selectedProduct.stock ||
      form.categoryId !== (selectedProduct.category?._id || '') ||
      form.material !== (selectedProduct.material || '') ||
      JSON.stringify(form.colors) !== JSON.stringify(selectedProduct.colors || []) ||
      Number(form.weight) !== (selectedProduct.weight || 0) ||
      Number(form.length) !== (selectedProduct.dimensions?.length || 0) ||
      Number(form.width) !== (selectedProduct.dimensions?.width || 0) ||
      Number(form.height) !== (selectedProduct.dimensions?.height || 0) ||
      form.isActive !== selectedProduct.isActive ||
      JSON.stringify(form.imageUrls) !== JSON.stringify(selectedProduct.images || [])
    );
  }, [form, isCreateMode, selectedProduct]);

  const addImageUrl = () => {
    const nextUrl = form.imageUrlInput.trim();
    if (!nextUrl) return;
    setForm((prev) => {
      if (prev.imageUrls.includes(nextUrl)) {
        return { ...prev, imageUrlInput: '' };
      }
      return {
        ...prev,
        imageUrls: [...prev.imageUrls, nextUrl],
        imageUrlInput: ''
      };
    });
  };

  const removeImageAt = (index: number) => {
    setForm((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index)
    }));
  };

  const addColor = () => {
    const nextColor = form.colorInput.trim();
    if (!nextColor) return;
    setForm((prev) => {
      if (prev.colors.some((color) => color.toLowerCase() === nextColor.toLowerCase())) {
        return { ...prev, colorInput: '' };
      }
      return {
        ...prev,
        colors: [...prev.colors, nextColor],
        colorInput: ''
      };
    });
  };

  const removeColor = (colorToRemove: string) => {
    setForm((prev) => ({
      ...prev,
      colors: prev.colors.filter((color) => color !== colorToRemove)
    }));
  };

  const handleSave = async () => {
    const mrp = Number(form.mrp);
    const salePrice = Number(form.salePrice);
    if (salePrice > mrp) {
      return;
    }

    const payload = {
      name: form.name,
      sku: form.sku,
      description: form.description,
      price: salePrice,
      mrp,
      salePrice,
      promoPrice: form.promoPrice.trim() ? Number(form.promoPrice) : null,
      promoStartsAt: form.promoStartsAt ? new Date(form.promoStartsAt).toISOString() : null,
      promoEndsAt: form.promoEndsAt ? new Date(form.promoEndsAt).toISOString() : null,
      stock: Number(form.stock),
      category: form.categoryId,
      material: form.material,
      weight: Number(form.weight),
      dimensions: {
        length: Number(form.length),
        width: Number(form.width),
        height: Number(form.height)
      },
      isEarring: false,
      style: [],
      colors: form.colors,
      isActive: form.isActive,
      images: form.imageUrls
    };

    setSaving(true);
    try {
      if (isCreateMode) {
        const { data } = await apiClient.post('/products', payload);
        const createdId = data?._id;
        dispatch(fetchProducts());
        if (createdId) {
          navigate(`${ROUTES.ADMIN}/products/${createdId}`);
          return;
        }
        navigate(`${ROUTES.ADMIN}/products`);
        return;
      }

      if (!selectedProduct) return;
      await apiClient.put(`/products/${selectedProduct._id}`, payload);
      await dispatch(fetchProductById(selectedProduct._id));
      dispatch(fetchProducts());
    } finally {
      setSaving(false);
    }
  };

  if (!isCreateMode && loading && !selectedProduct) {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Skeleton variant="rounded" height={140} />
        <Skeleton variant="rounded" height={40} width={420} />
        <Skeleton variant="rounded" height={340} />
      </Box>
    );
  }

  if (!isCreateMode && !selectedProduct) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Product not found
        </Typography>
        <MuiButton variant="outlined" onClick={() => navigate(`${ROUTES.ADMIN}/products`)}>
          Back to products
        </MuiButton>
      </Box>
    );
  }

  const product = selectedProduct ?? undefined;

  return (
    <Box>
      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          bgcolor: '#f9fafb',
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            <Box
              component="button"
              sx={{ p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', color: 'inherit' }}
              onClick={() => navigate(`${ROUTES.ADMIN}/products`)}
            >
              Products
            </Box>{' '}
            {'>'} {isCreateMode ? 'New product' : product?.name}
          </Typography>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {isCreateMode ? 'Create product' : product?.name}
          </Typography>
          {!isCreateMode && (
            <Typography variant="body2" color="text.secondary">
              SKU: {product?.sku}
            </Typography>
          )}
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={form.isActive ? 'Active' : 'Inactive'}
              color={form.isActive ? 'success' : 'default'}
            />
            <Chip size="small" label={`Stock: ${form.stock || 0}`} variant="outlined" />
          </Box>
          {isCreateMode ? (
            <PriceDisplay
              item={{ price: Number(form.salePrice || 0), salePrice: Number(form.salePrice || 0), mrp: Number(form.mrp || 0) }}
              priceVariant="body1"
              mrpVariant="body2"
              sx={{ mt: 1 }}
            />
          ) : (
            product && <PriceDisplay item={product} priceVariant="body1" mrpVariant="body2" sx={{ mt: 1 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <MuiButton variant="outlined" onClick={() => navigate(`${ROUTES.ADMIN}/products`)}>
            Back
          </MuiButton>
          <MuiButton variant="contained" disabled={!hasChanges || saving} onClick={handleSave}>
            {saving ? 'Saving...' : isCreateMode ? 'Create product' : 'Save changes'}
          </MuiButton>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="General" />
        <Tab label="Inventory & Pricing" />
        <Tab label="SEO" />
        <Tab label="Media" />
      </Tabs>

      <Box sx={{ p: 3, borderRadius: 3, bgcolor: '#f9fafb' }}>
        {tab === 0 && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label="Product name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={4}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="SKU"
                value={form.sku}
                onChange={(e) => setForm((prev) => ({ ...prev, sku: e.target.value }))}
                fullWidth
              />
              <TextField
                select
                label="Category"
                value={form.categoryId}
                onChange={(e) => setForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                fullWidth
              >
                {categories.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                label="MRP"
                type="number"
                value={form.mrp}
                onChange={(e) => setForm((prev) => ({ ...prev, mrp: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Sale price"
                type="number"
                value={form.salePrice}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, salePrice: e.target.value, price: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Stock"
                type="number"
                value={form.stock}
                onChange={(e) => setForm((prev) => ({ ...prev, stock: e.target.value }))}
                fullWidth
              />
            </Box>
            {Number(form.salePrice || 0) > Number(form.mrp || 0) && (
              <Typography variant="body2" color="error.main">
                Sale price cannot be greater than MRP.
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Scheduled sale (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              A temporary price for this product only — reverts automatically outside the window.
              For a store- or category-wide sale, use &quot;Run a sale&quot; on the products list.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Sale price (₹)"
                type="number"
                value={form.promoPrice}
                onChange={(e) => setForm((prev) => ({ ...prev, promoPrice: e.target.value }))}
                helperText="Empty = no scheduled sale"
                fullWidth
              />
              <TextField
                label="Starts"
                type="datetime-local"
                value={form.promoStartsAt}
                onChange={(e) => setForm((prev) => ({ ...prev, promoStartsAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                disabled={!form.promoPrice.trim()}
                helperText="Empty = immediately"
                fullWidth
              />
              <TextField
                label="Ends"
                type="datetime-local"
                value={form.promoEndsAt}
                onChange={(e) => setForm((prev) => ({ ...prev, promoEndsAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                disabled={!form.promoPrice.trim()}
                helperText="Empty = until removed"
                fullWidth
              />
            </Box>
            {form.promoPrice.trim() !== '' &&
              Number(form.promoPrice) >= Number(form.salePrice || 0) && (
                <Typography variant="body2" color="error.main">
                  Scheduled sale price must be below the regular sale price.
                </Typography>
              )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Material"
                value={form.material}
                onChange={(e) => setForm((prev) => ({ ...prev, material: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Weight (g)"
                type="number"
                value={form.weight}
                onChange={(e) => setForm((prev) => ({ ...prev, weight: e.target.value }))}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Color option"
                  value={form.colorInput}
                  onChange={(e) => setForm((prev) => ({ ...prev, colorInput: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addColor();
                    }
                  }}
                  fullWidth
                />
                <MuiButton variant="outlined" onClick={addColor}>
                  Add
                </MuiButton>
              </Box>
              {form.colors.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {form.colors.map((color) => (
                    <Chip
                      key={color}
                      label={color}
                      onDelete={() => removeColor(color)}
                      deleteIcon={<DeleteOutline />}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Add at least one color option for this product.
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Length"
                type="number"
                value={form.length}
                onChange={(e) => setForm((prev) => ({ ...prev, length: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Width"
                type="number"
                value={form.width}
                onChange={(e) => setForm((prev) => ({ ...prev, width: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Height"
                type="number"
                value={form.height}
                onChange={(e) => setForm((prev) => ({ ...prev, height: e.target.value }))}
                fullWidth
              />
            </Box>
            <TextField
              select
              label="Visibility"
              value={form.isActive ? 'active' : 'inactive'}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, isActive: e.target.value === 'active' }))
              }
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label="Meta title"
              value={form.metaTitle}
              onChange={(e) => setForm((prev) => ({ ...prev, metaTitle: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Meta description"
              value={form.metaDescription}
              onChange={(e) => setForm((prev) => ({ ...prev, metaDescription: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Slug"
              value={form.slug}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              fullWidth
            />
            <Divider />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Google preview
              </Typography>
              <Typography color="primary.main">{form.metaTitle || form.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {window.location.origin}/products/{form.slug}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {form.metaDescription || form.description}
              </Typography>
            </Box>
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Image URL"
                value={form.imageUrlInput}
                onChange={(e) => setForm((prev) => ({ ...prev, imageUrlInput: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addImageUrl();
                  }
                }}
                fullWidth
              />
              <MuiButton variant="outlined" onClick={addImageUrl}>
                Add
              </MuiButton>
            </Box>
            <MuiButton component="label" variant="outlined" disabled={uploading} sx={{ justifyContent: 'flex-start' }}>
              {uploading ? 'Uploading...' : 'Upload images'}
              <input
                hidden
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = '';
                  if (files.length === 0) return;
                  setCropQueue(files);
                }}
              />
            </MuiButton>
            <ImageCropDialog
              files={cropQueue}
              open={cropQueue.length > 0}
              onCancel={() => setCropQueue([])}
              onComplete={async (blobs) => {
                setCropQueue([]);
                setUploading(true);
                try {
                  const formData = new FormData();
                  blobs.forEach((blob, i) => formData.append('images', blob, `crop-${i}.jpg`));
                  const { data } = await apiClient.post<{ urls: string[] }>('/uploads', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                  });
                  setForm((prev) => ({ ...prev, imageUrls: [...prev.imageUrls, ...data.urls] }));
                } catch (err) {
                  console.error('Upload failed:', err);
                } finally {
                  setUploading(false);
                }
              }}
            />
            {form.imageUrls.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(4, minmax(0, 1fr))'
                  },
                  gap: 1
                }}
              >
                {form.imageUrls.map((img, index) => (
                  <Box key={`${img}-${index}`} sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={resolveImageUrl(img)}
                      alt={`Preview ${index + 1}`}
                      sx={{
                        width: '100%',
                        height: 120,
                        objectFit: 'cover',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        bgcolor: 'rgba(255,255,255,0.92)'
                      }}
                      onClick={() => removeImageAt(index)}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                    {index === 0 && (
                      <Chip
                        label="Primary"
                        size="small"
                        color="secondary"
                        sx={{ position: 'absolute', left: 6, bottom: 6 }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminProductDetailPage;
